import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Button } from "../../utils/components/ui/button";
import { Input } from "../../utils/components/ui/input";
import { Label } from "../../utils/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../utils/components/ui/select";
import {
  BranchFormSchema,
  BranchSchemaT,
} from "../../types/schema/branchSchema";
import { useAddBranch, useEditBranch } from "../../service/branchService";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Branch } from "../../types/branchType";

const buildInitialFormData = (branch?: Branch): BranchSchemaT => ({
  orgId:
    branch?.orgId ??
    (localStorage.getItem("orgId") ? Number(localStorage.getItem("orgId")) : 0),
  branchName: branch?.branchName ?? "",
  address: branch?.address ?? "",
  location: branch?.location ?? "",
  phone: branch?.phone ?? "",
  email: branch?.email ?? "",
  accountNumber: branch?.accountNumber ?? 0,
  type: branch?.type ?? "SHOP",
  isActive: branch?.isActive ?? true,
});

interface AddEditBranchFormProps {
  onClose: () => void;
  editBranch?: Branch;
}

const AddEditBranchForm = ({ onClose, editBranch }: AddEditBranchFormProps) => {
  const isEditMode = !!editBranch;

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BranchSchemaT>({
    resolver: zodResolver(BranchFormSchema),
    defaultValues: buildInitialFormData(editBranch),
  });

  const queryClient = useQueryClient();
  const addBranchMutation = useAddBranch();
  const editBranchMutation = useEditBranch();

  const isPending = addBranchMutation.isPending || editBranchMutation.isPending;

  const onFormSubmit = (formValues: BranchSchemaT) => {
    const payload: BranchSchemaT = {
      ...formValues,
      branchName: formValues.branchName.trim(),
      address: formValues.address.trim(),
      location: formValues.location.trim(),
      phone: formValues.phone.trim(),
      email: formValues.email.trim(),
    };

    const handleSuccess = (data: unknown) => {
      toast.success(
        isEditMode
          ? "Branch updated successfully!"
          : "Branch added successfully!",
      );
      console.log(isEditMode ? "Branch updated:" : "Branch added:", data);
      queryClient.invalidateQueries({ queryKey: ["useGetAllBranches"] });
      onClose();
      reset(buildInitialFormData());
    };

    const handleError = (error: Error) => {
      toast.error(
        isEditMode
          ? `Error updating branch: ${error.message}`
          : `Error adding branch: ${error.message}`,
      );
      console.error(error);
    };

    if (isEditMode && editBranch) {
      editBranchMutation.mutate(
        { branchId: editBranch.branchId, postData: payload },
        { onSuccess: handleSuccess, onError: handleError },
      );
    } else {
      addBranchMutation.mutate(payload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    }
  };

  return (
    <div className="w-[min(100vw,450px)]">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        {isEditMode ? "Edit Branch/Warehouse" : "Add Branch/Warehouse"}
      </h2>

      <form className="space-y-4" onSubmit={handleSubmit(onFormSubmit)}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="orgId">Org ID</Label>
            <Input
              id="orgId"
              type="number"
              readOnly
              className="bg-gray-100"
              {...register("orgId", { valueAsNumber: true })}
              required
            />
            {errors.orgId && (
              <p className="text-xs text-red-600">{errors.orgId.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="accountNumber">Account Number</Label>
            <Input
              id="accountNumber"
              type="number"
              {...register("accountNumber", { valueAsNumber: true })}
              required
            />
            {errors.accountNumber && (
              <p className="text-xs text-red-600">
                {errors.accountNumber.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="branchName">Branch Name</Label>
          <Input
            id="branchName"
            placeholder="Enter branch name"
            {...register("branchName")}
            required
          />
          {errors.branchName && (
            <p className="text-xs text-red-600">{errors.branchName.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="address">Address</Label>
          <Input
            id="address"
            placeholder="Enter address"
            {...register("address")}
            required
          />
          {errors.address && (
            <p className="text-xs text-red-600">{errors.address.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="Enter location"
              {...register("location")}
              required
            />
            {errors.location && (
              <p className="text-xs text-red-600">{errors.location.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              placeholder="Enter phone number"
              {...register("phone")}
              required
            />
            {errors.phone && (
              <p className="text-xs text-red-600">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email"
              {...register("email")}
              required
            />
            {errors.email && (
              <p className="text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Type</Label>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SHOP">SHOP</SelectItem>
                    <SelectItem value="WAREHOUSE">WAREHOUSE</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && (
              <p className="text-xs text-red-600">{errors.type.message}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Input
            id="isActive"
            type="checkbox"
            {...register("isActive")}
            className="h-4 w-4"
          />
          <Label htmlFor="isActive">Is Active</Label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onClose();
              reset(buildInitialFormData());
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            className="bg-[#1A5EF9] text-white hover:bg-[#0E4FDE]"
          >
            {isPending
              ? isEditMode
                ? "Saving..."
                : "Adding..."
              : isEditMode
                ? "Save Changes"
                : "Save Branch"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditBranchForm;
